import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IAppSetting } from '../../Interfaces/IAppSetting';
import { customFetcher } from '../useFetch';
import { IApp } from '../../Interfaces/IApp';
import { IExtension } from '../../Interfaces/IExtension';

export const useGetAppArray = () => {
  const {
    data: appArray,
    isLoading: isLoadingAppArray,
    refetch: refetchAppArray,
  } = useQuery<IApp[]>({
    queryKey: ['app'],
    queryFn: () => customFetcher({ endpoint: '/app' }),
  });

  return { appArray, isLoadingAppArray, refetchAppArray };
};

export const useGetSettingAppArray = () => {
  const {
    data: appSettingArray,
    isLoading: isLoadingAppSettingArray,
    refetch: refetchAppSettingArray,
  } = useQuery<IAppSetting[]>({
    queryKey: ['appsetting'],
    queryFn: () => customFetcher({ endpoint: '/appsetting' }),
  });

  return { appSettingArray, isLoadingAppSettingArray, refetchAppSettingArray };
};

export const useBulkUploadAppSetting = () => {
  const {
    mutateAsync: bulkUplaodAppSetting,
    isPending: isPendingBulkUploadAppSetting,
  } = useMutation({
    mutationFn: (appSetting: IAppSetting[]) =>
      customFetcher({
        endpoint: '/appsetting/bulk_upload',
        method: 'POST',
        body: appSetting,
      }),
  });
  return {
    bulkUplaodAppSetting,
    isPendingBulkUploadAppSetting,
  };
};

export const useGetExtensions = () => {
  const {
    data: extensionArray,
    isLoading: isLoadingExtensionArray,
    refetch: refetchExtensionArray,
  } = useQuery<IExtension[]>({
    queryKey: ['app/marketplace'],
    queryFn: () => customFetcher({ endpoint: '/app/marketplace' }),
  });

  return { extensionArray, isLoadingExtensionArray, refetchExtensionArray };
};

export const useActivateApp = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: activateApp, isPending: isPendingActivateApp } =
    useMutation({
      mutationFn: (system_key: string) =>
        customFetcher({
          endpoint: '/app/activate',
          method: 'POST',
          body: { system_key },
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['openformulare', 'status'],
        });
        queryClient.invalidateQueries({ queryKey: ['app'] });
      },
    });
  return { activateApp, isPendingActivateApp };
};

export const useDeactivateApp = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: deactivateApp, isPending: isPendingDeactivateApp } =
    useMutation({
      mutationFn: (system_key: string) =>
        customFetcher({
          endpoint: '/app/deactivate',
          method: 'POST',
          body: { system_key },
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['openformulare', 'status'],
        });
        queryClient.invalidateQueries({ queryKey: ['app'] });
      },
    });
  return { deactivateApp, isPendingDeactivateApp };
};

export const useGetAppSettingsByApp = (idApp: number | null) => {
  const enabled = idApp !== null && idApp !== undefined;
  const {
    data: settings,
    isLoading: isLoadingSettings,
    refetch: refetchSettings,
  } = useQuery<IAppSetting[]>({
    queryKey: ['app', idApp, 'setting'],
    queryFn: () => customFetcher({ endpoint: `/app/${idApp}/setting` }),
    enabled,
  });
  return { settings, isLoadingSettings, refetchSettings };
};

export const useUpsertAppSettings = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: upsertSettings, isPending: isPendingUpsertSettings } =
    useMutation({
      mutationFn: ({
        idApp,
        values,
      }: {
        idApp: number;
        values: Record<string, string>;
      }) =>
        customFetcher({
          endpoint: `/app/${idApp}/setting`,
          method: 'PUT',
          body: values,
        }),
      onSuccess: () => {
        // Erweiterungs-spezifische Status-Queries invalidieren, sodass
        // abhaengige Views (z. B. Bewertungsbogen-Warnung) sofort
        // neu pruefen statt am Cache zu hängen.
        queryClient.invalidateQueries({
          queryKey: ['openformulare', 'status'],
        });
        queryClient.invalidateQueries({ queryKey: ['app'] });
      },
    });
  return { upsertSettings, isPendingUpsertSettings };
};
